import { useEffect, useRef, useState } from 'react';
import * as L from 'leaflet';
// Eklenti yuklenirken L.Map'e 'gestureHandling' isleyicisini KENDISI kaydeder (addInitHook);
// burada ikinci kez kaydedilmez, yoksa her haritada iki isleyici olusur.
import 'leaflet-gesture-handling';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import {
  GoogleMapsApi,
  LatLngLiteral,
  hasGoogleMapsAuthFailed,
  loadGoogleMaps,
  onGoogleMapsAuthFailure,
} from './googleMaps';

export type LocationMapProps = {
  /** Liman konumu: harita buraya ortalanir, isaretci (igne + logo) bu noktaya baglanir */
  position: LatLngLiteral;
  zoom: number;
  /** Google Maps JavaScript API anahtari; bos ise anahtarsiz uydu katmani (Leaflet + Esri) */
  apiKey?: string;
  title: string;
  logoSrc: string;
  logoAlt: string;
  /** Arayuz dili: kontrol etiketleri/ipuclari ve hareket uyarisi (Google modunda betigin ilk yuklemesi) */
  locale: string;
};

/** Harita gorunumleri: Google gommesindeki "Uydu" (hibrit) ve "Harita" (sokak haritasi) */
export type BaseMapKind = 'satellite' | 'map';

const ESRI_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services';

/**
 * Atif cubugu oneki: Leaflet ve Esri'nin basemap kullaniminda istedigi "Powered by Esri"
 * (esri-leaflet ile ayni). Etkin katmanlarin kaynak listeleri bunun arkasina eklenir.
 */
export const ATTRIBUTION_PREFIX =
  '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a> | Powered by <a href="https://www.esri.com/">Esri</a>';

/**
 * Esri referans katmanlarinin (yollar ve yer adlari) kaynak listesi; iki katman ayni metni
 * tasidigindan Leaflet atif cubugunda bir kez yazar. Metin servislerin `MapServer?f=pjson`
 * copyrightText alanindan; OSM turevi oldugundan ODbL baglantisi zorunludur.
 */
const REFERENCE_ATTRIBUTION =
  'Roads &amp; labels: Esri, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, and the GIS user community';

/** Opak karo katmanlari (uydu, sokak haritasi): website.css'teki karo birlesim duzeltmesi yalnizca bunlara */
export const OPAQUE_TILES_CLASS = 'location-map-tiles-opaque';

/**
 * Anahtarsiz modun "Uydu" gorunumu: uydu goruntusu + yol agi + yer adlari (Google hibrit
 * gorunumune en yakin birlesim). Esri servisleri API anahtari istemez; atif (attribution)
 * zorunludur ve haritanin sag altinda gosterilir. Saglayici degisirse yalnizca burasi.
 * roadsUrl (World_Transportation) ve labelsUrl (World_Boundaries_and_Places) Esri'de "mature
 * support" durumundadir (guncellenmiyor, Aralik 2029'da emekli); ayrinti README "Konum haritasi"
 * bolumunde. Bos birakilan katman eklenmez.
 */
export const SATELLITE_TILES = {
  url: `${ESRI_TILES}/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
  attribution: 'Imagery: Esri, Vantor, Earthstar Geographics, and the GIS User Community',
  roadsUrl: `${ESRI_TILES}/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}`,
  roadsAttribution: REFERENCE_ATTRIBUTION,
  labelsUrl: `${ESRI_TILES}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`,
  labelsAttribution: REFERENCE_ATTRIBUTION,
  maxZoom: 18,
  minZoom: 3,
};

/**
 * Anahtarsiz modun "Harita" gorunumu: Esri sokak haritasi (yer adlari yerel dilde, Turkiye'de
 * Turkce). Bu raster servis de "mature support" durumundadir (Aralik 2029'da emekli); README.
 */
export const STREET_TILES = {
  url: `${ESRI_TILES}/World_Street_Map/MapServer/tile/{z}/{y}/{x}`,
  attribution:
    'Map: Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, Esri China (Hong Kong), Esri Korea, Esri (Thailand), NGCC, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, and the GIS User Community',
};

/**
 * Harita/Uydu dugmesindeki kucuk onizlemenin karo seviyesi. 9. seviye (~80 km) Iskenderun
 * Korfezi + Adana ovasi + Amanoslar'i birlikte gosterir; daha yakin seviyelerde limanin
 * karosu neredeyse tamamen deniz oldugundan onizleme duz mavi kalir.
 */
export const PREVIEW_TILE_ZOOM = 9;

/** +/- dugmelerinin ipucu ve erisilebilirlik etiketleri (Leaflet varsayilani Ingilizce) */
export function zoomControlTitles(locale: string) {
  if (locale.toLowerCase().startsWith('en')) {
    return { zoomInTitle: 'Zoom in', zoomOutTitle: 'Zoom out' };
  }
  return { zoomInTitle: 'Yakınlaştır', zoomOutTitle: 'Uzaklaştır' };
}

/** Harita/Uydu dugmesinin metinleri: dugme etkin OLMAYAN gorunumun adini tasir (Google gibi) */
export function baseMapTexts(locale: string): Record<BaseMapKind, { label: string; title: string }> {
  if (locale.toLowerCase().startsWith('en')) {
    return {
      satellite: { label: 'Satellite', title: 'Show satellite imagery' },
      map: { label: 'Map', title: 'Show street map' },
    };
  }
  return {
    satellite: { label: 'Uydu', title: 'Uydu görüntüsünü göster' },
    map: { label: 'Harita', title: 'Sokak haritasını göster' },
  };
}

/** Hareket uyarisi metinleri (eklentinin kendi cevirisi tarayici diline bakar; site dili esas alinir) */
export function gestureTexts(locale: string) {
  if (locale.toLowerCase().startsWith('en')) {
    return {
      touch: 'Use two fingers to move the map',
      scroll: 'Use ctrl + scroll to zoom the map',
      scrollMac: 'Use ⌘ + scroll to zoom the map',
    };
  }
  return {
    touch: 'Haritayı kaydırmak için iki parmak kullanın',
    scroll: 'Haritayı yakınlaştırmak için ctrl + kaydırma kullanın',
    scrollMac: 'Haritayı yakınlaştırmak için ⌘ + kaydırma kullanın',
  };
}

/**
 * Portal'dan gelen "Harita Enlem/Boylam" metinlerini koordinata cevirir.
 * Virgullu ondalik kabul edilir; gecersiz veya aralik disi degerde yedek konum doner.
 */
export function resolveMapPosition(
  lat: string | undefined,
  lng: string | undefined,
  fallback: LatLngLiteral,
): LatLngLiteral {
  const parse = (value: string | undefined) => {
    const text = (value || '').trim().replace(',', '.');
    if (!text) return NaN;
    return Number(text);
  };
  const parsedLat = parse(lat);
  const parsedLng = parse(lng);
  const valid =
    Number.isFinite(parsedLat) &&
    Number.isFinite(parsedLng) &&
    Math.abs(parsedLat) <= 90 &&
    Math.abs(parsedLng) <= 180;
  return valid ? { lat: parsedLat, lng: parsedLng } : fallback;
}

/**
 * Koordinatin Web Mercator (XYZ) karo adresi: Harita/Uydu dugmesindeki onizleme icin, ilgili
 * servisin liman cevresindeki tek karosu kullanilir (ek istek yok, anahtar yok).
 */
export function tileUrlAt(template: string, position: LatLngLiteral, zoom: number): string {
  const count = 2 ** zoom;
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  const latRad = (clamp(position.lat, -85.05112878, 85.05112878) * Math.PI) / 180;
  const x = clamp(Math.floor(((position.lng + 180) / 360) * count), 0, count - 1);
  const y = clamp(
    Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * count),
    0,
    count - 1,
  );
  return template.replace('{z}', String(zoom)).replace('{x}', String(x)).replace('{y}', String(y));
}

const PIN_SVG =
  '<svg viewBox="0 0 28 42" width="28" height="42" aria-hidden="true" focusable="false">' +
  '<path d="M14 1C6.82 1 1 6.82 1 14c0 9.75 13 27 13 27s13-17.25 13-27C27 6.82 21.18 1 14 1z" fill="#ea4335" stroke="#b3261e" stroke-width="1"/>' +
  '<circle cx="14" cy="14" r="5" fill="#a52714"/>' +
  '</svg>';

/**
 * Her iki modda ayni isaretci: kirmizi konum ignesi (ucu tam koordinatta) ve hemen altinda
 * TBH rozeti. CSS (.location-map-pin*) ignenin ucunu (0,0) noktasina, rozeti onun altina koyar.
 */
function createMarkerElement(logoSrc: string, logoAlt: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'location-map-pin';

  const pin = document.createElement('span');
  pin.className = 'location-map-pin-icon';
  pin.innerHTML = PIN_SVG;
  wrapper.appendChild(pin);

  const badge = document.createElement('div');
  badge.className = 'location-map-logo location-map-logo--anchored';
  const img = document.createElement('img');
  img.src = logoSrc;
  img.alt = logoAlt;
  badge.appendChild(img);
  wrapper.appendChild(badge);
  return wrapper;
}

type BaseMapTexts = Record<BaseMapKind, { label: string; title: string }>;

type BaseMapControlSettings = {
  texts: BaseMapTexts;
  /** Onizleme gorselleri (karo adresi) */
  previews: Record<BaseMapKind, string>;
  onSelect: (kind: BaseMapKind) => void;
};

/**
 * Google gommesindeki sol alt "Harita/Uydu" dugmesi: etkin OLMAYAN gorunumun kucuk onizlemesi ve
 * adi gosterilir; tiklaninca o gorunume gecilir. Leaflet kontrol kosesine (bottomleft) eklenir.
 */
class BaseMapControl extends L.Control {
  private readonly onSelect: (kind: BaseMapKind) => void;
  private texts: BaseMapTexts;
  private previews: Record<BaseMapKind, string>;
  private active: BaseMapKind = 'satellite';
  private button: HTMLButtonElement | null = null;
  private preview: HTMLSpanElement | null = null;
  private label: HTMLSpanElement | null = null;

  constructor(settings: BaseMapControlSettings) {
    super({ position: 'bottomleft' });
    this.onSelect = settings.onSelect;
    this.texts = settings.texts;
    this.previews = settings.previews;
  }

  onAdd(): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'location-map-type';
    const preview = document.createElement('span');
    preview.className = 'location-map-type-preview';
    preview.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.className = 'location-map-type-label';
    button.appendChild(preview);
    button.appendChild(label);
    // Dugme uzerindeki tiklama/cift tiklama/surukleme/tekerlek haritaya gecmesin
    L.DomEvent.disableClickPropagation(button);
    L.DomEvent.disableScrollPropagation(button);
    button.addEventListener('click', () => {
      this.onSelect(this.inactive());
    });
    this.button = button;
    this.preview = preview;
    this.label = label;
    this.render();
    return button;
  }

  onRemove(): void {
    this.button = null;
    this.preview = null;
    this.label = null;
  }

  setActive(kind: BaseMapKind): void {
    this.active = kind;
    this.render();
  }

  setPreviews(previews: Record<BaseMapKind, string>): void {
    this.previews = previews;
    this.render();
  }

  /** Site dili degisince (harita yeniden kurulmaz) */
  setTexts(texts: BaseMapTexts): void {
    this.texts = texts;
    this.render();
  }

  private inactive(): BaseMapKind {
    return this.active === 'satellite' ? 'map' : 'satellite';
  }

  private render(): void {
    if (!this.button || !this.preview || !this.label) return;
    const target = this.inactive();
    const text = this.texts[target];
    this.label.textContent = text.label;
    this.button.title = text.title;
    this.button.setAttribute('aria-label', text.title);
    this.button.dataset.target = target;
    this.preview.style.backgroundImage = `url("${this.previews[target]}")`;
  }
}

type MapHandle = {
  setView: (position: LatLngLiteral, zoom: number) => void;
  /** Isaretciyi yeni koordinata tasir (harita yeniden kurulmaz) */
  setPosition: (position: LatLngLiteral) => void;
  /** Kontrol metinlerini site diline cevirir (harita yeniden kurulmaz) */
  setLocale: (locale: string) => void;
  destroy: () => void;
};

/**
 * Google modu: harita + OverlayView ile koordinata bagli isaretci. fromLatLngToDivPixel haritayla
 * birlikte kayan katmana gore piksel verir; isaretci surukleme/yakinlastirmada limanin uzerinde kalir.
 */
function mountGoogleMap(
  api: GoogleMapsApi,
  container: HTMLElement,
  position: LatLngLiteral,
  zoom: number,
  logoSrc: string,
  logoAlt: string,
): MapHandle {
  const map = new api.Map(container, {
    center: position,
    zoom,
    mapTypeId: 'hybrid',
    // Gommedeki yerlesim: Harita/Uydu sol altta, +/- sag altta (yeni kamera kontrolu yerine klasik +/-)
    mapTypeControl: true,
    mapTypeControlOptions: { position: api.ControlPosition?.LEFT_BOTTOM },
    zoomControl: true,
    zoomControlOptions: { position: api.ControlPosition?.RIGHT_BOTTOM },
    cameraControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    gestureHandling: 'cooperative',
  });

  const marker = createMarkerElement(logoSrc, logoAlt);
  let latLng = new api.LatLng(position.lat, position.lng);
  const overlay = new api.OverlayView();
  overlay.onAdd = () => {
    overlay.getPanes()?.floatPane.appendChild(marker);
  };
  overlay.draw = () => {
    const point = overlay.getProjection()?.fromLatLngToDivPixel(latLng);
    if (!point) return;
    marker.style.left = `${point.x}px`;
    marker.style.top = `${point.y}px`;
  };
  overlay.onRemove = () => {
    marker.remove();
  };
  overlay.setMap(map);

  return {
    setView: (next, nextZoom) => {
      map.setCenter(next);
      map.setZoom(nextZoom);
    },
    setPosition: (next) => {
      latLng = new api.LatLng(next.lat, next.lng);
      overlay.draw?.();
    },
    // Google'in arayuz dili betik yuklenirken belirlenir; sayfa yenilenmeden degismez (README)
    setLocale: () => undefined,
    destroy: () => {
      overlay.setMap(null);
      // Google haritasi yok edilemez; DOM'u temizlenir, sonraki kurulum sifirdan yapar
      container.replaceChildren();
    },
  };
}

/**
 * Anahtarsiz mod: Leaflet + Esri katmanlari. Isaretci sifir boyutlu bir divIcon'dur; Leaflet
 * onu koordinata bagli tutar (surukleme, ctrl + tekerlek, +/- ve iki parmakla yakinlastirma).
 * Kontroller Google gommesindeki yerlesimde: sol altta Harita/Uydu, sag altta +/-, en altta atif.
 */
function mountLeafletMap(
  container: HTMLElement,
  position: LatLngLiteral,
  zoom: number,
  logoSrc: string,
  logoAlt: string,
  locale: string,
): MapHandle {
  const map = L.map(container, {
    center: [position.lat, position.lng],
    zoom,
    minZoom: SATELLITE_TILES.minZoom,
    maxZoom: SATELLITE_TILES.maxZoom,
    // +/- site dilinde ipucu/erisilebilirlik etiketiyle asagida eklenir
    zoomControl: false,
    attributionControl: true,
    gestureHandling: true,
    gestureHandlingOptions: { duration: 1500, text: gestureTexts(locale) },
  });
  const zoomControl = L.control.zoom({ position: 'bottomright', ...zoomControlTitles(locale) }).addTo(map);
  // Leaflet'in varsayilan oneki yerine Leaflet + "Powered by Esri"
  map.attributionControl?.setPrefix(ATTRIBUTION_PREFIX);

  const satelliteLayers: L.Layer[] = [
    L.tileLayer(SATELLITE_TILES.url, {
      maxZoom: SATELLITE_TILES.maxZoom,
      attribution: SATELLITE_TILES.attribution,
      className: OPAQUE_TILES_CLASS,
    }),
  ];
  if (SATELLITE_TILES.roadsUrl) {
    satelliteLayers.push(
      L.tileLayer(SATELLITE_TILES.roadsUrl, {
        maxZoom: SATELLITE_TILES.maxZoom,
        attribution: SATELLITE_TILES.roadsAttribution,
      }),
    );
  }
  if (SATELLITE_TILES.labelsUrl) {
    satelliteLayers.push(
      L.tileLayer(SATELLITE_TILES.labelsUrl, {
        maxZoom: SATELLITE_TILES.maxZoom,
        attribution: SATELLITE_TILES.labelsAttribution,
      }),
    );
  }
  const baseMaps: Record<BaseMapKind, L.Layer> = {
    satellite: L.layerGroup(satelliteLayers),
    map: L.tileLayer(STREET_TILES.url, {
      maxZoom: SATELLITE_TILES.maxZoom,
      attribution: STREET_TILES.attribution,
      className: OPAQUE_TILES_CLASS,
    }),
  };
  let activeBaseMap: BaseMapKind = 'satellite';
  baseMaps.satellite.addTo(map);

  const previewsFor = (at: LatLngLiteral): Record<BaseMapKind, string> => ({
    satellite: tileUrlAt(SATELLITE_TILES.url, at, PREVIEW_TILE_ZOOM),
    map: tileUrlAt(STREET_TILES.url, at, PREVIEW_TILE_ZOOM),
  });
  const baseMapControl = new BaseMapControl({
    texts: baseMapTexts(locale),
    previews: previewsFor(position),
    onSelect: (kind) => {
      if (kind === activeBaseMap) return;
      baseMaps[activeBaseMap].remove();
      baseMaps[kind].addTo(map);
      activeBaseMap = kind;
      baseMapControl.setActive(kind);
    },
  });
  baseMapControl.addTo(map);

  const icon = L.divIcon({
    className: 'location-map-marker',
    html: createMarkerElement(logoSrc, logoAlt),
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
  const marker = L.marker([position.lat, position.lng], { icon, interactive: false, keyboard: false }).addTo(map);

  // Kapsayicinin yuksekligi sol sutuna bagli (grid stretch): yazi tipi gelince veya dil
  // degisince boyut degisir ama window 'resize' atilmaz; Leaflet boyutu kendisi izlemez.
  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          map.invalidateSize({ animate: false });
        })
      : null;
  resizeObserver?.observe(container);

  return {
    setView: (next, nextZoom) => {
      map.setView([next.lat, next.lng], nextZoom);
    },
    setPosition: (next) => {
      marker.setLatLng([next.lat, next.lng]);
      baseMapControl.setPreviews(previewsFor(next));
    },
    setLocale: (nextLocale) => {
      baseMapControl.setTexts(baseMapTexts(nextLocale));

      // +/- : Leaflet baslik ve aria-label'i kurulumda yazar; yerinde guncellenir
      const titles = zoomControlTitles(nextLocale);
      const zoomRoot = zoomControl.getContainer();
      const zoomIn = zoomRoot?.querySelector<HTMLElement>('.leaflet-control-zoom-in');
      const zoomOut = zoomRoot?.querySelector<HTMLElement>('.leaflet-control-zoom-out');
      if (zoomIn) {
        zoomIn.title = titles.zoomInTitle;
        zoomIn.setAttribute('aria-label', titles.zoomInTitle);
      }
      if (zoomOut) {
        zoomOut.title = titles.zoomOutTitle;
        zoomOut.setAttribute('aria-label', titles.zoomOutTitle);
      }

      // Hareket uyarisi: eklenti metni kapsayicinin data ozniteliklerinden okur (::after content);
      // Mac ayrimi eklentiyle ayni (navigator.platform). Secenek de guncellenir ki isleyici
      // yeniden acilirsa ayni metni kullansin.
      const texts = gestureTexts(nextLocale);
      const mac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      container.setAttribute('data-gesture-handling-touch-content', texts.touch);
      container.setAttribute('data-gesture-handling-scroll-content', mac ? texts.scrollMac : texts.scroll);
      map.options.gestureHandlingOptions = { ...map.options.gestureHandlingOptions, text: texts };
    },
    destroy: () => {
      resizeObserver?.disconnect();
      // Eklentinin removeHooks'u surukleme/tekerlek isleyicilerini yeniden ACAR; map.remove()
      // icindeki _clearHandlers once dragging'i kapatip sonra eklentiyi kapattigi icin kapsayicida
      // olu haritaya ait mousedown/wheel dinleyicileri kalir (StrictMode yeniden kurulumu ve Google
      // moduna gecis: "Cannot read properties of null (reading 'offsetWidth')"). Once eklenti kapatilir.
      map.gestureHandling?.disable();
      map.remove();
      map.dragging?.disable();
      map.scrollWheelZoom?.disable();
    },
  };
}

export function LocationMap({ position, zoom, apiKey, title, logoSrc, logoAlt, locale }: LocationMapProps) {
  const key = (apiKey || '').trim();
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const useGoogle = key !== '' && failedKey !== key;
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<MapHandle | null>(null);
  const { lat, lng } = position;

  // Konum/yakinlastirma/dil degisince harita yeniden kurulmaz; kurulum efektleri ref'ten okur
  const viewRef = useRef({ lat, lng, zoom });
  viewRef.current = { lat, lng, zoom };
  const localeRef = useRef(locale);
  localeRef.current = locale;

  // Google modu (anahtar varsa): yalnizca anahtar degisince yeniden kurulur
  useEffect(() => {
    if (!useGoogle) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    const fallBack = () => {
      if (!cancelled) setFailedKey(key);
    };
    if (hasGoogleMapsAuthFailed()) {
      fallBack();
      return undefined;
    }
    const unsubscribe = onGoogleMapsAuthFailure(fallBack);

    loadGoogleMaps(key, localeRef.current)
      .then((api) => {
        if (cancelled) return;
        const view = viewRef.current;
        handleRef.current = mountGoogleMap(api, container, { lat: view.lat, lng: view.lng }, view.zoom, logoSrc, logoAlt);
      })
      .catch(fallBack);

    return () => {
      cancelled = true;
      unsubscribe();
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [useGoogle, key, logoSrc, logoAlt]);

  // Anahtarsiz mod (Leaflet): anahtar yoksa, betik gelmezse veya anahtar yetkisizse
  useEffect(() => {
    if (useGoogle) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const view = viewRef.current;
    handleRef.current = mountLeafletMap(
      container,
      { lat: view.lat, lng: view.lng },
      view.zoom,
      logoSrc,
      logoAlt,
      localeRef.current,
    );
    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [useGoogle, logoSrc, logoAlt]);

  // Portal koordinati/yakinlastirmasi sonradan gelirse harita yerinde guncellenir
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    handle.setView({ lat, lng }, zoom);
    handle.setPosition({ lat, lng });
  }, [lat, lng, zoom]);

  // Basliktaki TR/EN gecisi: kontrol metinleri yerinde cevrilir
  useEffect(() => {
    handleRef.current?.setLocale(locale);
  }, [locale]);

  return <div ref={containerRef} className="location-map-frame" role="region" aria-label={title} />;
}
